/** Data older than two blotter refreshes is stale. Three failed reads in a row is offline. */
const OMS_STALE_MS = 60_000;
const OMS_OFFLINE_FAILURES = 3;

export type OmsLiveState = 'live' | 'stale' | 'offline' | 'paused';

export function omsLiveState(input: { asOf: string | null; failures: number; now: number; paused?: boolean }): OmsLiveState | null {
  if (input.failures >= OMS_OFFLINE_FAILURES) return 'offline';
  if (input.paused) return 'paused';
  if (!input.asOf) return null;
  const at = Date.parse(input.asOf);
  if (Number.isNaN(at)) return null;
  return input.now - at > OMS_STALE_MS ? 'stale' : 'live';
}

/** `13 Aug 2026 12:58:07` in the given zone. The zone defaults to the user's and is not written out. */
export function formatAsOf(iso: string, timeZone?: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  const day = pick('day');
  const month = pick('month');
  const year = pick('year');
  const hour = pick('hour');
  const minute = pick('minute');
  const second = pick('second');
  if (!day || !month || !year || !hour || !minute || !second) return null;
  return `${day} ${month} ${year} ${hour}:${minute}:${second}`;
}

/** `12:58:07` from the same clock as `formatAsOf`. */
export function formatAsOfClock(iso: string): string | null {
  const full = formatAsOf(iso);
  return full ? full.slice(-8) : null;
}

/** Outside production. `uat` + HF Demo is the badge `UAT / HF DEMO`. */
export function omsEnvBadge(env: string | undefined, datasource: string | undefined): string | null {
  const stage = (env ?? '').trim().toLowerCase();
  if (!stage || stage === 'prod') return null;
  const source = (datasource ?? '').trim().toLowerCase();
  const sourceLabel = source === 'hfdemo' ? 'HF DEMO' : source.toUpperCase();
  return sourceLabel ? `${stage.toUpperCase()} / ${sourceLabel}` : stage.toUpperCase();
}
